/**
 * Intake Chats admin page — every landing-page chat conversation
 * (completed AND abandoned), with full transcripts and a clear
 * "needs contact" signal.
 *
 * The Leads page only shows finalized conversations (a Lead row is
 * written at close); this page surfaces the chat sessions themselves so
 * the operator can read what was actually said, spot visitors who
 * dropped off mid-conversation after leaving an email, and work the
 * follow-up queue. "Mark contacted" updates the linked lead's status.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCw,
  X,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { updateLead } from '../services/leadAdminService';

interface ChatSummary {
  id: number;
  session_id: string;
  audience?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  completed: boolean;
  completed_at?: string | null;
  final_tier?: 'A' | 'B' | 'C' | null;
  tier_reasoning?: string | null;
  name?: string | null;
  organization?: string | null;
  email?: string | null;
  website?: string | null;
  role?: string | null;
  use_case?: string | null;
  preferred_call_time?: string | null;
  message_count: number;
  user_turns: number;
  utm_source?: string | null;
  lead_id?: number | null;
  lead_status?: string | null;
  needs_contact: boolean;
}

interface ChatDetail extends ChatSummary {
  messages: Array<{ role: string; content: string; ts?: string }>;
  extracted: Record<string, any>;
  validations: Record<string, any>;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  referrer?: string | null;
  user_agent?: string | null;
}

interface ChatList {
  items: ChatSummary[];
  total: number;
  needs_contact_total: number;
  page: number;
  size: number;
  pages: number;
}

type FilterMode = 'all' | 'needs_contact' | 'completed' | 'abandoned';

const TIER_STYLES: Record<string, string> = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-gray-200 text-gray-700',
};

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const IntakeChatsPage: React.FC = () => {
  const [list, setList] = useState<ChatList | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>('needs_contact');
  const [tier, setTier] = useState<string>('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, size: 25 };
      if (filter === 'needs_contact') params.needs_contact = true;
      if (filter === 'completed') params.completed = true;
      if (filter === 'abandoned') params.completed = false;
      if (tier) params.tier = tier;
      const resp = await api.get<ChatList>('/intake-chats', { params });
      setList(resp.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load intake chats');
    } finally {
      setLoading(false);
    }
  }, [page, filter, tier]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const resp = await api.get<ChatDetail>(`/intake-chats/${id}`);
      setDetail(resp.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load transcript');
    } finally {
      setDetailLoading(false);
    }
  };

  const markContacted = async (leadId: number) => {
    setMarking(true);
    const result = await updateLead(leadId, { status: 'contacted' });
    setMarking(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Marked contacted');
    setDetail(null);
    load();
  };

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-blue-600" />
              Intake Chats
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Every landing-page conversation — including visitors who
              dropped off mid-chat. {list ? `${list.needs_contact_total} need contact.` : ''}
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {([
            ['needs_contact', 'Needs contact'],
            ['all', 'All'],
            ['completed', 'Completed'],
            ['abandoned', 'Abandoned'],
          ] as Array<[FilterMode, string]>).map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => { setFilter(mode); setPage(1); }}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${
                filter === mode
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="mx-2 h-5 w-px bg-gray-300" />
          {['A', 'B', 'C'].map((t) => (
            <button
              key={t}
              onClick={() => { setTier(tier === t ? '' : t); setPage(1); }}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                tier === t
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Tier {t}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {loading && !list ? (
            <div className="flex items-center justify-center gap-2 p-10 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : !list || list.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              No conversations match this filter.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Visitor</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Msgs</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openDetail(c.id)}
                    className="cursor-pointer hover:bg-blue-50/50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {c.name || '(no name)'}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Building2 className="h-3 w-3" />
                        {c.organization || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-700">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {c.email || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {c.final_tier ? (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TIER_STYLES[c.final_tier]}`}>
                          {c.final_tier}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">in progress</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.needs_contact ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Needs contact
                        </span>
                      ) : c.completed ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                          Handled
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">abandoned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.user_turns}/{c.message_count}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.utm_source || c.audience || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {fmtDate(c.created_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {list && list.pages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>Page {list.page} of {list.pages} ({list.total} total)</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={page >= list.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transcript drawer */}
      {(detail || detailLoading) && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setDetail(null)}
        >
          <div
            className="flex h-full w-full max-w-xl flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading || !detail ? (
              <div className="flex flex-1 items-center justify-center gap-2 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading transcript…
              </div>
            ) : (
              <>
                <div className="border-b border-gray-200 px-5 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {detail.name || '(no name)'}
                        {detail.final_tier && (
                          <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold align-middle ${TIER_STYLES[detail.final_tier]}`}>
                            Tier {detail.final_tier}
                          </span>
                        )}
                      </h2>
                      <p className="mt-0.5 text-sm text-gray-500">
                        {detail.organization || '—'}
                        {detail.role ? ` · ${detail.role}` : ''}
                        {detail.audience ? ` · ${detail.audience}` : ''}
                      </p>
                      {detail.email && (
                        <a
                          href={`mailto:${detail.email}`}
                          className="mt-1 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <Mail className="h-3.5 w-3.5" /> {detail.email}
                        </a>
                      )}
                      {detail.preferred_call_time && (
                        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          <Calendar className="h-3 w-3" />
                          Wants a call: {detail.preferred_call_time}
                        </p>
                      )}
                      {detail.tier_reasoning && (
                        <p className="mt-1 text-xs text-gray-400">
                          Scoring: {detail.tier_reasoning}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setDetail(null)}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {detail.needs_contact && detail.lead_id && (
                      <button
                        disabled={marking}
                        onClick={() => markContacted(detail.lead_id!)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {marking
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <CheckCircle2 className="h-4 w-4" />}
                        Mark contacted
                      </button>
                    )}
                    {detail.lead_id && (
                      <Link
                        to={`/leads/${detail.lead_id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <ExternalLink className="h-4 w-4" /> View lead
                      </Link>
                    )}
                  </div>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
                  {detail.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                          m.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        {m.content}
                        {m.ts && (
                          <div className={`mt-1 text-[10px] ${m.role === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                            {fmtDate(m.ts)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default IntakeChatsPage;
